import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { authAPI } from './services/api';
import { firebaseAuth, firebaseEnabled } from './services/firebase';
import { browserLocalPersistence, browserSessionPersistence, setPersistence,
  signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut,
  sendPasswordResetEmail, createUserWithEmailAndPassword, updateProfile,
  sendEmailVerification } from 'firebase/auth';

const AuthContext = createContext(null);
const LANDING_ORIGIN = process.env.REACT_APP_LANDING_URL || 'https://indicatorai.io';

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true; // If we can't decode, assume it's expired
  }
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      let token = (window.sessionStorage.getItem('indicatorai_token') || window.localStorage.getItem('indicatorai_token'));
      if (token && isTokenExpired(token)) {
        window.localStorage.removeItem('indicatorai_token');
        window.sessionStorage.removeItem('indicatorai_token');
        token = null;
      }
      if (!token) {
        try {
          const response = await fetch(new URL('/api/auth/session', LANDING_ORIGIN), {credentials:'include', cache:'no-store', ...(typeof AbortSignal.timeout === 'function' ? {signal:AbortSignal.timeout(10000)} : {})});
          if (response.ok) {
            const session = await response.json();
            if (typeof session.access_token === 'string' && !isTokenExpired(session.access_token)) {
              token = session.access_token;
              window.sessionStorage.setItem('indicatorai_token', token);
            }
          }
        } catch { /* No landing session: show the sign-in entry point. */ }
      }
      if (!token) {
        setIsLoading(false);
        return;
      }

      // First check if token is expired locally
      if (isTokenExpired(token)) {
        window.localStorage.removeItem('indicatorai_token');
        window.sessionStorage.removeItem('indicatorai_token');
        setIsLoading(false);
        return;
      }

      try {
        // Validate token by making a test API call
        await api.get('/auth/me');
        setIsAuthenticated(true);
      } catch (error) {
        // Token is invalid, remove it
        window.localStorage.removeItem('indicatorai_token');
        window.sessionStorage.removeItem('indicatorai_token');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, []);

  const finishFirebaseLogin = async (credential, remember) => {
    try {
      const response = await authAPI.exchangeFirebaseToken(await credential.user.getIdToken());
      const token = response.data?.access_token;
      if (!token) throw new Error('Missing application token');
      const storage = remember ? window.localStorage : window.sessionStorage;
      window.localStorage.removeItem('indicatorai_token');
      window.sessionStorage.removeItem('indicatorai_token');
      storage.setItem('indicatorai_token', token);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      await signOut(firebaseAuth);
      throw error;
    }
  };

  const loginGoogle = async (remember = true) => {
    await setPersistence(firebaseAuth, remember ? browserLocalPersistence : browserSessionPersistence);
    return finishFirebaseLogin(await signInWithPopup(firebaseAuth, new GoogleAuthProvider()), remember);
  };

  const resetPassword = (email) => sendPasswordResetEmail(firebaseAuth, email);

  const fetchRegistrationPolicy = useCallback(async () => {
    const response = await api.get('/auth/registration-policy');
    return response.data;
  }, []);

  const persistAccessToken = (token, remember) => {
    window.localStorage.removeItem('indicatorai_token');
    window.sessionStorage.removeItem('indicatorai_token');
    (remember ? window.localStorage : window.sessionStorage).setItem('indicatorai_token', token);
    setIsAuthenticated(true);
  };

  const register = useCallback(async (payload, remember = true) => {
    const {
      username, email, password, phone, termsAccepted, termsVersion,
    } = payload;
    if (firebaseEnabled) {
      const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
      const name = username?.trim();
      if (name) {
        await updateProfile(credential.user, { displayName: name });
      }
      try {
        await sendEmailVerification(credential.user);
      } finally {
        await signOut(firebaseAuth);
      }
      return { emailVerificationSent: true };
    }
    const response = await api.post('/auth/register', {
      username,
      email,
      password,
      ...(phone ? { phone } : {}),
      terms_accepted: termsAccepted,
      terms_version: termsVersion,
    });
    const token = response.data?.access_token;
    if (!token) {
      throw new Error('Missing application token');
    }
    persistAccessToken(token, remember);
    return { emailVerificationSent: false };
  }, []);

  const login = async (username, password, remember = true) => {
    if (firebaseEnabled) {
      await setPersistence(firebaseAuth, remember ? browserLocalPersistence : browserSessionPersistence);
      const credential = await signInWithEmailAndPassword(firebaseAuth, username, password);
      if (!credential.user.emailVerified) {
        try {
          await sendEmailVerification(credential.user);
        } finally {
          await signOut(firebaseAuth);
        }
        const err = new Error('email-not-verified');
        err.code = 'auth/email-not-verified';
        throw err;
      }
      return finishFirebaseLogin(credential, remember);
    }
    const response = await api.post('/auth/login', { username, password });
    const token = response.data?.access_token;
    if (!token) {
      return false;
    }
    window.localStorage.removeItem('indicatorai_token');
    window.sessionStorage.removeItem('indicatorai_token');
    (remember ? window.localStorage : window.sessionStorage).setItem('indicatorai_token', token);
    setIsAuthenticated(true);
    return true;
  };

  const logout = async () => {
    try { await fetch(new URL('/api/auth/session', LANDING_ORIGIN), {method:'DELETE', credentials:'include'}); } catch { /* Local logout still completes if landing is unavailable. */ }
    setIsAuthenticated(false);
    window.localStorage.removeItem('indicatorai_token');
    window.sessionStorage.removeItem('indicatorai_token');
    if (firebaseAuth) signOut(firebaseAuth).catch(() => { /* Application session is already cleared. */ });
  };

  if (isLoading) {
    return <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '18px'
    }}>
      Loading IndicatorAI...
    </div>;
  }

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      login,
      logout,
      loginGoogle,
      resetPassword,
      register,
      fetchRegistrationPolicy,
      firebaseEnabled,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = { children: PropTypes.node.isRequired };

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
